'use client'

import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { StoreLocatorSwitcher } from 'maps/components/StoreLocator/StoreLocatorSwitcher/StoreLocatorSwitcher'
import { MapProvider } from 'maps/contexts/MapProvider'
import { useStoreDetail } from 'maps/services/client/useStoreDetail/useStoreDetail'
import { type Location, MAP_TYPE, type Store } from 'maps/types'
import {
	filterAndSortByNearbyStore,
	refreshDistance,
} from 'maps/utils/distance/distance'
import { useSearchParams } from 'next/navigation'

import styles from './StoreLocatorPageContainer.module.scss'

interface StoreLocatorPageProps {
	readonly storesFromLite: Store[]
}

export const StoreLocatorPageContainer = ({
	storesFromLite,
}: StoreLocatorPageProps) => {
	const { t } = useLabels()
	const searchParams = useSearchParams()
	const storeId = searchParams.get('storeId')

	const { data: storeDetail } = useStoreDetail({ storeId })
	const storeLocation: Location | undefined = storeDetail && {
		coordinates: {
			lat: storeDetail.addresses.coordinates.lat,
			lng: storeDetail.addresses.coordinates.lng,
		},
		addressInfo: {
			address: storeDetail.addresses.address,
			postalCode: storeDetail.addresses.postalCode,
			cityName: storeDetail.addresses.city,
		},
	}

	const filteredStoresByLocation = storeLocation
		? filterAndSortByNearbyStore(
				refreshDistance(storesFromLite, storeLocation),
				true
			)
		: storesFromLite

	const storesBrands = Array.from(
		new Set(storesFromLite?.flatMap((item) => item.collections))
	)

	const brandsOptions = [
		{
			value: t('storeFinder.search.stores.label'),
			text: t('storeLocator.search.selector.all.option'),
		},
		...storesBrands.map((brand) => ({
			value: brand,
			text: brand,
		})),
	]

	if (storeId && !storeDetail) {
		return null
	}

	return (
		<MapProvider
			initialState={{
				stores: filteredStoresByLocation,
				filteredStores: filteredStoresByLocation,
				expanded: storeDetail,
				storesBrand: storesFromLite ? brandsOptions : undefined,
				searchInput: storeLocation?.addressInfo?.cityName,
				location: storeLocation,
				type: MAP_TYPE.STORES,
				hasDistanceLimit: true,
				onModal: true,
			}}
		>
			<div className={styles.storeLocatorPageContainer}>
				<StoreLocatorSwitcher />
			</div>
		</MapProvider>
	)
}
